// External Dependencies
import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import Cookies from 'js-cookie'

// Icons
import { MdOutlineDelete } from 'react-icons/md'
import { CiEdit } from 'react-icons/ci'
import { FiExternalLink } from 'react-icons/fi'

// Components
import Button from '../Custom/Buttons/Button'
import CustomToast from '../Custom/CustomToast'

// Utils and Hooks
import axiosInterceptorInstance from '../../utils/axios/axiosInterceptorInstance'
import { useDebounce } from '../../hooks/useDebounce'
import configManager from '../../config/configManager'

// Assets
import brokenImg from '../../assets/images/brokenImg.png'

// Material Categories Configuration
const MATERIAL_CATEGORIES = [
  { value: null, label: 'Select Category' },
  { value: 'Stone', label: 'Stone' },
  { value: 'Brick', label: 'Brick' },
  { value: 'Wood', label: 'Wood' },
  { value: 'Terrazzo', label: 'Terrazzo' },
  { value: 'Concrete', label: 'Concrete' },
  { value: 'Metal', label: 'Metal' },
  { value: 'Tile', label: 'Tile' },
  { value: 'Fabric', label: 'Fabric' },
  { value: 'Wallpaper', label: 'Wallpaper' },
  { value: 'Carpet', label: 'Carpet' },
  { value: 'Surfaces', label: 'Surfaces' },
  { value: 'Plastic', label: 'Plastic' },
  { value: 'Finishes', label: 'Finishes' },
  { value: 'Organic', label: 'Organic' },
  { value: 'Acoustic', label: 'Acoustic' },
  { value: 'Insulation', label: 'Insulation' },
  { value: 'Landscaping', label: 'Landscaping' },
]

const Materials = ({ userPermissions }) => {
  // Config and Constants
  const configData = configManager()
  const ITEMS_PER_PAGE = 50

  /** Create editor link for this material */
  const getEditorLink = (material) => {
    const id = material._id || material.id
    const token = Cookies.get('admin_token') || ''
    const params = new URLSearchParams({ material_id: id })
    if (token) params.set('admin_token', token)
    return `${configData.BACKEND_URL}/create?${params.toString()}`
  }

  // State Management
  const [loading, setLoading] = useState(false)
  const [category, setSelectedCategory] = useState('') // Changed from null to empty string
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [materials, setMaterials] = useState([])
  const [moreAvailable, setMoreAvailable] = useState(true)
  const [deleteMaterials, setDeleteMaterials] = useState([])
  const [isDeleting, setIsDeleting] = useState(false)

  // Search debouncing
  const debouncedSearchTerm = useDebounce(search, 300)

  // Fetch materials when search or category changes
  useEffect(() => {
    setPage(1)
    setMaterials([])
    fetchMaterials({
      page: 1,
      limit: ITEMS_PER_PAGE,
      category:
        category === 'Select Category' || category === '' ? null : category, // Updated condition
      search: debouncedSearchTerm,
    })
  }, [debouncedSearchTerm, category])

  // API Handlers
  const fetchMaterials = async (params) => {
    const urlParams = new URLSearchParams({
      page: params.page,
      limit: params.limit,
      front: false,
      ...(params.category && { category: params.category }),
      ...(params.search && { search: params.search }),
    })

    setLoading(true)
    try {
      const { data } = await axiosInterceptorInstance.post(
        `/materials?${urlParams.toString()}`,
        { body: JSON.stringify(params) },
      )

      if (data?.status === 'success') {
        setMaterials((prev) =>
          params.page === 1 ? data.results : [...prev, ...data.results],
        )
        setMoreAvailable(data.more)
      }
    } catch (error) {
      CustomToast({
        message: 'Failed to fetch materials. Please try again.',
        type: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteMaterials = async () => {
    if (!userPermissions.includes('material:delete')) {
      CustomToast({
        message: 'You do not have permission to delete materials.',
        type: 'error',
      })
      return
    }

    if (!deleteMaterials.length) {
      CustomToast({
        message: 'Please select materials to delete.',
        type: 'warning',
      })
      return
    }

    setIsDeleting(true)
    try {
      const response = await axiosInterceptorInstance.delete(
        '/materials/delete',
        {
          data: { deleteMaterials },
        },
      )

      if (response.data.success) {
        setMaterials((prev) =>
          prev.filter((material) => !deleteMaterials.includes(material._id)),
        )
        CustomToast({
          message: 'Materials deleted successfully.',
          type: 'success',
        })
      }
    } catch (error) {
      CustomToast({
        message: 'Failed to delete materials. Please try again.',
        type: 'error',
      })
    } finally {
      setIsDeleting(false)
      setDeleteMaterials([])
    }
  }

  // Event Handlers
  const handleLoadMore = () => {
    const nextPage = page + 1
    setPage(nextPage)
    fetchMaterials({
      page: nextPage,
      limit: ITEMS_PER_PAGE,
      category: category === 'Select Category' ? null : category,
      search: debouncedSearchTerm,
    })
  }

  const handleSelectForDelete = (materialId) => {
    if (!userPermissions.includes('material:delete')) return

    setDeleteMaterials((prev) =>
      prev.includes(materialId)
        ? prev.filter((id) => id !== materialId)
        : [...prev, materialId],
    )
  }

  const handleSelectAllForDelete = () => {
    if (!userPermissions.includes('material:delete')) return

    setDeleteMaterials((prev) =>
      prev.length === materials.length
        ? []
        : materials.map((material) => material._id),
    )
  }

  // Render Methods
  const renderDeleteControls = () => {
    if (
      !deleteMaterials.length ||
      !userPermissions.includes('material:delete')
    ) {
      return null
    }

    return (
      <>
        <Button
          label='Delete'
          disabled={isDeleting}
          isLoading={isDeleting}
          onClick={handleDeleteMaterials}
          className='flex gap-1.5 items-center font-medium rounded-lg disabled:pointer-events-none disabled:opacity-70 border-2 !border-red-700 hover:!bg-red-700 !text-red-700 hover:!text-white'
        >
          <MdOutlineDelete className='text-2xl' />
        </Button>

        <label className='flex gap-2 items-center font-semibold'>
          <input
            type='checkbox'
            className='w-5 h-5'
            checked={deleteMaterials.length === materials.length}
            onChange={handleSelectAllForDelete}
          />
          Select All
        </label>
      </>
    )
  }

  return (
    <div>
      <div className='flex gap-4 justify-start items-center flex-wrap'>
        {renderDeleteControls()}

        <div>
          <select
            className='w-full border rounded-lg px-3 py-3 focus:outline-none focus:ring-2 focus:ring-prime'
            value={category}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            {MATERIAL_CATEGORIES.map((cat) => (
              <option key={cat.value || 'default'} value={cat.value || ''}>
                {cat.label}
              </option>
            ))}
          </select>
        </div>

        <input
          type='text'
          placeholder='Search materials...'
          className='w-full sm:w-[300px] ml-auto border-[2px] border-prime rounded-lg p-3 focus:outline-none focus:border-orange'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {loading && <p className='text-center my-20'>Loading...</p>}

      {!loading && materials.length === 0 && (
        <p className='text-center my-10'>No materials available.</p>
      )}

      <div className='grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 gap-4 my-8'>
        {materials.map((material) => (
          <div key={material._id} className='rounded'>
            <div className='rounded-xl shadow-md overflow-hidden h-full flex flex-col justify-between bg-white border border-gray-100'>
              <div className='relative group'>
                {userPermissions.includes('material:update') && (
                  <Link
                    to={`/material/update/${material._id}`}
                    className='absolute bottom-2 right-2 bg-prime p-2 text-xl hidden text-white rounded-full group-hover:inline-block shadow-md'
                    title='Edit Material Details'
                  >
                    <CiEdit />
                  </Link>
                )}

                {userPermissions.includes('material:delete') && (
                  <input
                    type='checkbox'
                    className='absolute top-2 right-2 w-5 h-5 cursor-pointer accent-prime'
                    checked={deleteMaterials.includes(material._id)}
                    onChange={() => handleSelectForDelete(material._id)}
                  />
                )}

                <img
                  src={
                    material.thumbnail
                      ? `${configData.CDN_BASE_URL}/${
                          material.thumbnail.startsWith('/')
                            ? material.thumbnail.slice(1)
                            : `thumbnails/${material.thumbnail}`
                        }`
                      : brokenImg
                  }
                  alt={material.name}
                  className='w-full aspect-[9/5] object-cover rounded-t-xl'
                  onError={(e) => {
                    e.target.src = brokenImg
                  }}
                />
              </div>

              <div className='px-3 py-2.5 flex items-center justify-between gap-2 border-t border-gray-100 bg-white'>
                <div className='flex flex-col min-w-0 flex-1'>
                  <h3 className='text-sm font-semibold text-gray-900 uppercase truncate' title={material.name}>
                    {material.name}
                  </h3>
                  <p className='text-gray-500 text-xs mt-0.5 truncate' title={material?.description}>
                    {material?.description || 'No description'}
                  </p>
                </div>

                <a
                  href={getEditorLink(material)}
                  target='_blank'
                  rel='noopener noreferrer'
                  title='Open in Texture Builder Editor'
                  className='flex-shrink-0 p-2 text-prime hover:text-white bg-prime/10 hover:bg-prime rounded-lg transition-colors border border-prime/20 flex items-center justify-center'
                >
                  <FiExternalLink className='text-lg' />
                </a>
              </div>
            </div>
          </div>
        ))}
      </div>

      {moreAvailable && !loading && (
        <div className='flex justify-center mt-8'>
          <Button
            label='Load More'
            onClick={handleLoadMore}
            className='bg-prime text-white hover:bg-prime/80'
          />
        </div>
      )}
    </div>
  )
}

export default Materials
